import React, { useEffect, useState, useMemo } from 'react';
import {
    Box, SimpleGrid, Text, Flex, HStack, Icon, Input, Select, Button, ButtonGroup, Spinner,
} from '@chakra-ui/react';
import {
    Chart as ChartJS,
    CategoryScale, LinearScale, BarElement, LineElement, PointElement,
    ArcElement, Title, Tooltip as ChartTooltip, Legend,
    BarController, LineController,
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import {
    FaSearch, FaBoxes, FaClock, FaUserTie, FaCheckCircle, FaExclamationTriangle,
    FaCalendarAlt, FaStore, FaShieldAlt, FaCommentDots, FaBalanceScale, FaChartLine,
} from 'react-icons/fa';
import { getReclamacoesMetricas } from '../../services/api';
import { formatBRL } from '../../utils/formatters';
import ChartCard from '../ExpandableChartCard';
import dayjs from 'dayjs';

ChartJS.register(
    CategoryScale, LinearScale, BarElement, LineElement, PointElement,
    ArcElement, Title, ChartTooltip, Legend, BarController, LineController
);

const CONTA_COLORS = { 1: '#F68633', 2: '#805AD5', 3: '#1baf7a' };

const truncate = (s, n) => (s && s.length > n ? s.substring(0, n - 1) + '…' : s || '');

const KpiCard = ({ icon, iconColor, title, value, footer, bg, borderColor, valueColor }) => (
    <Box bg={bg || 'white'} p={{ base: 3, md: 4 }} borderRadius="lg" boxShadow="sm" borderWidth="1px"
        borderColor={borderColor || 'gray.200'} transition="all 0.15s" _hover={{ boxShadow: 'md', transform: 'translateY(-1px)' }}>
        <HStack spacing={2} mb={1}>
            {icon && <Icon as={icon} color={iconColor || 'gray.500'} boxSize={4} />}
            <Text fontSize="11px" fontWeight="bold" color="gray.600" letterSpacing="wider" textTransform="uppercase" noOfLines={1}>{title}</Text>
        </HStack>
        <Text fontSize={{ base: 'xl', md: '2xl' }} fontWeight="extrabold" color={valueColor || 'gray.800'} lineHeight="1.1">{value}</Text>
        {footer && <Text fontSize="xs" color="gray.500" mt={0.5} noOfLines={1}>{footer}</Text>}
    </Box>
);

const EmptyState = ({ text }) => (
    <Flex h="100%" align="center" justify="center"><Text color="gray.400" fontSize="sm">{text}</Text></Flex>
);

const PRESETS = [
    { label: '30 dias', days: 30 },
    { label: '90 dias', days: 90 },
    { label: '6 meses', days: 180 },
    { label: '1 ano', days: 365 },
    { label: 'Tudo', days: null },
];

const ReclamacoesMetricas = () => {
    const hoje = dayjs().format('YYYY-MM-DD');
    const [dataInicial, setDataInicial] = useState(dayjs().subtract(12, 'month').format('YYYY-MM-DD'));
    const [dataFinal, setDataFinal] = useState(hoje);
    const [conta, setConta] = useState('');
    const [origem, setOrigem] = useState('');
    const [dados, setDados] = useState(null);
    const [loading, setLoading] = useState(true);
    const [modoVendas, setModoVendas] = useState('qtd');

    const carregar = async () => {
        setLoading(true);
        const params = {};
        if (dataInicial) params.data_inicial = dataInicial;
        if (dataFinal) params.data_final = dataFinal;
        if (conta) params.conta = conta;
        if (origem) params.origem = origem;
        const resp = await getReclamacoesMetricas(params);
        setDados(resp);
        setLoading(false);
    };

    useEffect(() => {
        carregar();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const aplicarPreset = (days) => {
        if (days === null) setDataInicial('2023-01-01');
        else setDataInicial(dayjs().subtract(days, 'day').format('YYYY-MM-DD'));
        setDataFinal(hoje);
    };

    const charts = useMemo(() => {
        if (!dados) return null;
        const volume = {
            labels: dados.volume_mensal.map((m) => m.label),
            datasets: [
                {
                    label: 'Abertas', data: dados.volume_mensal.map((m) => m.abertas),
                    borderColor: '#E53E3E', backgroundColor: 'rgba(229,62,62,0.15)',
                    tension: 0.3, fill: true, pointRadius: 3, pointHoverRadius: 6,
                },
                {
                    label: 'Finalizadas', data: dados.volume_mensal.map((m) => m.finalizadas),
                    borderColor: '#38A169', backgroundColor: 'rgba(56,161,105,0.15)',
                    tension: 0.3, fill: true, pointRadius: 3, pointHoverRadius: 6,
                },
            ],
        };
        const contas = [...dados.por_conta].sort((a, b) => a.conta - b.conta);
        const porConta = {
            labels: contas.map((c) => `Conta ${c.conta}`),
            datasets: [{
                label: 'Reclamações', data: contas.map((c) => c.qtd),
                backgroundColor: contas.map((c) => CONTA_COLORS[c.conta] || '#A0AEC0'), borderRadius: 6,
            }],
        };
        const motivos = {
            labels: dados.top_motivos.map((m) => truncate(m.motivo, 45)),
            datasets: [{
                label: 'Ocorrências', data: dados.top_motivos.map((m) => m.qtd),
                backgroundColor: '#3182CE', borderRadius: 4,
            }],
        };
        const produtos = {
            labels: (dados.top_produtos || []).map((p) => truncate(p.nome, 45)),
            datasets: [{
                label: 'Reclamações', data: (dados.top_produtos || []).map((p) => p.qtd),
                backgroundColor: '#805AD5', borderRadius: 4,
            }],
        };
        return { volume, porConta, motivos, produtos };
    }, [dados]);

    const vendasVsRecChart = useMemo(() => {
        if (!dados?.vendas_vs_reclamacoes?.length) return null;
        const vd = dados.vendas_vs_reclamacoes;
        const isQtd = modoVendas === 'qtd';
        const mat = vd.map((m) => m.maturando);
        return {
            labels: vd.map((m) => m.label),
            datasets: [
                {
                    type: 'bar',
                    label: isQtd ? 'Volume de vendas' : 'Vendas (R$)',
                    yAxisID: 'y', order: 2,
                    data: vd.map((m) => (isQtd ? m.vendas_qtd : m.vendas_valor)),
                    backgroundColor: '#CBD5E0', borderRadius: 4,
                },
                {
                    type: 'bar',
                    label: isQtd ? 'Geraram reclamação' : 'Reclamado (R$)',
                    yAxisID: 'y', order: 1,
                    data: vd.map((m) => (isQtd ? m.reclamadas_qtd : m.reclamadas_valor)),
                    backgroundColor: mat.map((x) => (x ? '#FBB6CE' : '#D53F8C')), borderRadius: 4,
                },
                {
                    type: 'line',
                    label: 'Taxa de reclamação %',
                    yAxisID: 'y1', order: 0,
                    data: vd.map((m) => (isQtd ? m.taxa_qtd : m.taxa_valor)),
                    borderColor: '#805AD5', backgroundColor: '#805AD5',
                    tension: 0.3, pointRadius: 4, pointHoverRadius: 6,
                    segment: { borderDash: (ctx) => (mat[ctx.p1DataIndex] ? [6, 4] : undefined) },
                },
            ],
        };
    }, [dados, modoVendas]);

    const optVendasVsRec = {
        responsive: true, maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
            legend: { position: 'bottom' },
            tooltip: {
                callbacks: {
                    label: (ctx) => {
                        const v = ctx.parsed.y;
                        if (ctx.dataset.yAxisID === 'y1') return `${ctx.dataset.label}: ${v}%`;
                        return `${ctx.dataset.label}: ${modoVendas === 'valor' ? formatBRL(v) : v}`;
                    },
                },
            },
        },
        scales: {
            y: {
                beginAtZero: true, position: 'left',
                title: { display: true, text: modoVendas === 'valor' ? 'R$' : 'Qtd' },
                ticks: { precision: 0, callback: modoVendas === 'valor' ? ((v) => formatBRL(v).replace('R$', '').trim()) : undefined },
            },
            y1: {
                beginAtZero: true, position: 'right', grid: { drawOnChartArea: false },
                title: { display: true, text: 'Taxa %' }, ticks: { callback: (v) => `${v}%` },
            },
        },
    };

    const optVertical = {
        responsive: true, maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
    };
    const optHorizontal = {
        responsive: true, maintainAspectRatio: false, indexAxis: 'y',
        plugins: { legend: { display: false } },
        scales: { x: { beginAtZero: true, ticks: { precision: 0 } } },
    };
    const optLine = {
        responsive: true, maintainAspectRatio: false,
        plugins: { legend: { position: 'bottom', labels: { boxWidth: 12, padding: 12 } } },
        scales: { y: { beginAtZero: true, ticks: { precision: 0 } } },
    };

    const k = dados?.kpis || {};

    return (
        <Box w="100%" mt={2}>
            <Box bg="gray.50" borderWidth="1px" borderColor="#FF9F59" borderRadius="lg" p={{ base: 3, md: 4 }} boxShadow="md" mb={4}>
                <Flex wrap="wrap" gap={3} align="flex-end">
                    <Box>
                        <Text fontSize="10px" fontWeight="bold" color="#F68633">PERÍODO</Text>
                        <HStack spacing={1}>
                            <Input size="sm" type="date" bg="white" value={dataInicial} max={dataFinal} onChange={(e) => setDataInicial(e.target.value)} w="145px" />
                            <Text fontSize="sm" color="gray.500">até</Text>
                            <Input size="sm" type="date" bg="white" value={dataFinal} min={dataInicial} onChange={(e) => setDataFinal(e.target.value)} w="145px" />
                        </HStack>
                    </Box>
                    <Box>
                        <Text fontSize="10px" fontWeight="bold" color="#F68633">ORIGEM</Text>
                        <Select size="sm" bg="white" value={origem} onChange={(e) => { const v = e.target.value; setOrigem(v); if (v && v !== 'ml') setConta(''); }} w="150px" title="Canal de venda de origem">
                            <option value="">Todas origens</option>
                            <option value="ml">Mercado Livre</option>
                            <option value="ecommerce">E-commerce</option>
                            <option value="taga">TAGA (manual)</option>
                        </Select>
                    </Box>
                    <Box>
                        <Text fontSize="10px" fontWeight="bold" color="#F68633">CONTA</Text>
                        <Select size="sm" bg="white" value={conta} onChange={(e) => setConta(e.target.value)} w="130px" isDisabled={origem !== '' && origem !== 'ml'}>
                            <option value="">Todas</option>
                            <option value="1">Conta 1</option>
                            <option value="2">Conta 2</option>
                            <option value="3">Conta 3</option>
                        </Select>
                    </Box>
                    <HStack spacing={1} alignSelf="flex-end">
                        {PRESETS.map((p) => (
                            <Button key={p.label} size="xs" variant="outline" colorScheme="orange" onClick={() => aplicarPreset(p.days)}>{p.label}</Button>
                        ))}
                    </HStack>
                    <Button size="sm" colorScheme="orange" leftIcon={<FaSearch />} onClick={carregar} isLoading={loading}>Aplicar</Button>
                </Flex>
            </Box>

            {loading && !dados ? (
                <Flex justify="center" align="center" h="400px"><Spinner size="xl" color="#F68633" thickness="4px" /></Flex>
            ) : !dados ? (
                <Box textAlign="center" p={10} bg="red.50" borderRadius="lg" borderWidth="1px" borderColor="red.200">
                    <Text color="red.700" fontWeight="bold">Erro ao carregar métricas</Text>
                </Box>
            ) : (
                <Flex direction="column" gap={4}>
                    <SimpleGrid columns={{ base: 2, md: 4, lg: 8 }} spacing={3}>
                        <KpiCard icon={FaBoxes} iconColor="gray.500" title="Total" value={k.total} footer="no período" valueColor="gray.700" />
                        <KpiCard icon={FaClock} iconColor="blue.500" title="Abertas" value={k.abertas} valueColor="blue.700" />
                        <KpiCard icon={FaCheckCircle} iconColor="green.500" title="Finalizadas" value={k.finalizadas} valueColor="green.700" />
                        <KpiCard icon={FaUserTie} iconColor="orange.500" bg="orange.50" borderColor="orange.200" title="Sua vez" value={k.sua_vez} footer="aguardando você" valueColor="orange.700" />
                        <KpiCard icon={FaExclamationTriangle} iconColor="red.500" bg="red.50" borderColor="red.200" title="Vencidas" value={k.vencidas} valueColor="red.700" />
                        <KpiCard icon={FaBalanceScale} iconColor="purple.500" title="Em mediação" value={k.em_mediacao} valueColor="purple.700" />
                        <KpiCard icon={FaShieldAlt} iconColor="red.500" title="Afeta reputação" value={k.afeta_reputacao} valueColor="red.700" />
                        <KpiCard icon={FaClock} iconColor="teal.500" title="Tempo médio" value={k.tempo_medio_resolucao_dias != null ? `${k.tempo_medio_resolucao_dias}d` : 'N/A'} footer="até encerrar" valueColor="teal.700" />
                    </SimpleGrid>

                    <ChartCard
                        title="Vendas × Reclamações por mês"
                        icon={FaChartLine}
                        height="380px"
                        headerRight={
                            <ButtonGroup size="xs" isAttached variant="outline">
                                <Button colorScheme="orange" variant={modoVendas === 'qtd' ? 'solid' : 'outline'} onClick={() => setModoVendas('qtd')}>
                                    Quantidade
                                </Button>
                                <Button colorScheme="orange" variant={modoVendas === 'valor' ? 'solid' : 'outline'} onClick={() => setModoVendas('valor')}>
                                    Valor (R$)
                                </Button>
                            </ButtonGroup>
                        }
                        footer={
                            <Text fontSize="11px" color="gray.500" mt={2}>
                                Reclamação (mediação) atribuída ao mês da <b>venda</b> (taxa real por safra). Linha tracejada e barra clara nos últimos ~2 meses indicam vendas ainda <b>em maturação</b> — podem gerar reclamações nas próximas semanas, então a taxa tende a subir.
                            </Text>
                        }
                    >
                        {vendasVsRecChart
                            ? <Bar data={vendasVsRecChart} options={optVendasVsRec} />
                            : <EmptyState text="Sem dados de vendas no período" />}
                    </ChartCard>

                    <ChartCard title="Volume mensal — Abertas vs Finalizadas" icon={FaCalendarAlt}>
                        {charts.volume.labels.length ? <Line data={charts.volume} options={optLine} /> : <EmptyState text="Sem dados no período" />}
                    </ChartCard>

                    <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
                        <ChartCard title="Top motivos das reclamações" icon={FaCommentDots} height="380px">
                            {charts.motivos.labels.length ? <Bar data={charts.motivos} options={optHorizontal} /> : <EmptyState text="Sem motivos registrados" />}
                        </ChartCard>
                        <ChartCard title="Top produtos com reclamações" icon={FaBoxes} height="380px">
                            {charts.produtos.labels.length ? <Bar data={charts.produtos} options={optHorizontal} /> : <EmptyState text="Sem produtos vinculados" />}
                        </ChartCard>
                    </SimpleGrid>

                    <ChartCard title="Reclamações por conta" icon={FaStore}>
                        <Bar data={charts.porConta} options={optVertical} />
                    </ChartCard>
                </Flex>
            )}
        </Box>
    );
};

export default ReclamacoesMetricas;
